import type { SizesAndQuantityItem } from "../types";
import { getRangeString } from "../ArtworkMultiSize/utils";
import { scaleToFitRatio, getRandomId } from "../utils";
import { MAX_PRINT_SIZES_GROUPED, DEFAULT_AUTO_SEED_PRESET } from "./constants";

/**
 * Subset of the DTF store fields the seed helper needs to make a decision.
 * Kept narrow so the helper is unit-testable without instantiating the full
 * Pinia store.
 */
type SeedStoreContext = {
  experiments?: Record<string, unknown>;
  isStandardArtwork?: boolean;
  printAsIs?: boolean;
  isSpangle?: boolean;
  // The DTF store types `sticker` as `unknown` because two unrelated stores
  // (the React DTF store and the Vue/Pinia stickers module) read the field
  // through this shape; only its truthiness is meaningful here.
  sticker?: unknown;
  isPuff?: boolean;
  isJerseyNumbers?: boolean;
};

type FallbackValues = {
  width: number | string;
  height: number | string;
  quantity: number;
};

type BuildSeedOptions = {
  store: SeedStoreContext;
  fallbackValues: FallbackValues;
  id: string;
  artworkAspectRatio?: number | null | undefined;
};

/**
 * Decides whether the empty-state seed should use the named-placement default
 * (Full Front, all sub-sizes scaled to artwork aspect ratio at quantity 0) or
 * fall back to the prior post-MN-4778 Custom-Size seed shape.
 *
 * Special product types (Spangle, Sticker, Puff, Jersey Numbers, Print-As-Is
 * gang sheet) have their own product-specific default values exposed through
 * `store.initialValues`. The flag-on path is scoped to standard DTF only so
 * those product-specific defaults are not regressed.
 */
export function isNamedPlacementSeedEnabled(store: SeedStoreContext): boolean {
  if (!store) return false;
  const flag = store.experiments?.["dtf_default_named_placement_seed"];
  if (flag !== 1 && flag !== true && flag !== "1") return false;
  if (!store.isStandardArtwork) return false;
  if (store.printAsIs) return false;
  if (store.isSpangle) return false;
  if (store.sticker) return false;
  if (store.isPuff) return false;
  if (store.isJerseyNumbers) return false;
  return true;
}

/**
 * Builds the post-enhancement seed written to `artwork.sizesAndQuantities`
 * by the WS handler in `dtf-channel.ts` once the upscaler finishes and the
 * customer has no customer-edited rows yet.
 *
 * Flag-on (named placement, standard DTF only):
 *   One row per sub-size in `MAX_PRINT_SIZES_GROUPED["full-front"]`
 *   (Adult XS-S … Infant Newborn), shape:
 *   `{ preset: "full-front", size, width, height, quantity: 0,
 *      isAutoSeed: true, id }`
 *   Mirrors what `handlePresetSelect({ preset: "full-front" })` emits so the
 *   `CustomSizeSelector` `zipArray(catalog, sizes)` zip is balanced — without
 *   this, only one catalog row would render and its label/dims would diverge
 *   from the seed. When the artwork aspect ratio is known, every row's
 *   dimensions are scaled via `scaleToFitRatio`; otherwise raw catalog
 *   dimensions are used.
 *
 * Flag-off (or any non-standard product type):
 *   `[{ preset: "custom", width, height, quantity, isAutoSeed: true, id }]`
 *   Identical to the post-MN-4778 contract — width/height/quantity come
 *   from the caller's fallback source (`store.initialValues`).
 */
export function buildDefaultPlacementSeed({
  store,
  fallbackValues,
  id,
  artworkAspectRatio
}: BuildSeedOptions): SizesAndQuantityItem[] {
  if (isNamedPlacementSeedEnabled(store)) {
    const aspectRatio =
      typeof artworkAspectRatio === "number" && artworkAspectRatio > 0 ? artworkAspectRatio : null;
    const catalog = MAX_PRINT_SIZES_GROUPED[DEFAULT_AUTO_SEED_PRESET.preset];

    return catalog.map(size => {
      const dims = aspectRatio
        ? scaleToFitRatio(size.width, size.height, aspectRatio)
        : { width: size.width, height: size.height };

      return {
        preset: DEFAULT_AUTO_SEED_PRESET.preset,
        size: getRangeString(size),
        width: Number(dims.width),
        height: Number(dims.height),
        quantity: 0,
        isAutoSeed: true,
        id: getRandomId()
      };
    });
  }

  return [
    {
      preset: "custom",
      width: Number(fallbackValues.width),
      height: fallbackValues.height,
      quantity: fallbackValues.quantity,
      isAutoSeed: true,
      id
    }
  ];
}

/**
 * Decides what `sizesAndQuantities` should be after the upscaler completes.
 * Pure function of `(store, existing, seedFn)` — extracted so the
 * idempotency contract (repeated WS events must not re-seed) is testable
 * without a Pinia store.
 *
 * Branches:
 *   1. `existing` empty → seed (first post-enhancement WS event)
 *   2. Any `existing` row that is NOT isAutoSeed (customer edited) →
 *      preserve untouched
 *   3. `existing` is exactly the pre-upload Custom placeholder
 *      (`[{ preset:"custom", isAutoSeed:true }]`) AND named-placement
 *      is enabled → swap for the flag-on seed (covers the rare path
 *      where a flag-off WS event seeded a single Custom row, then the
 *      flag was enabled, then a subsequent WS event lands)
 *   4. Any other auto-seed-only existing (e.g. the 12-row Full Front
 *      seed already applied) → preserve untouched
 *
 * Branch 4 is the idempotency guard: WS-driven `updateArtworkValuesById`
 * does not flip `isSizesEdited`, so without the narrowing in branch 3
 * every subsequent WS tick (fetch_delivery_date, downstream
 * markEnhancementCompleted) would re-seed with fresh `getRandomId()`s,
 * unmounting the 12 React rows mid-flow while the customer is picking
 * a size.
 */
export function resolveSeedAfterEnhancement(
  store: SeedStoreContext,
  existing: Record<string, unknown>[],
  seedFn: () => Record<string, unknown>[]
): Record<string, unknown>[] {
  if (existing.length === 0) return seedFn();

  const hasUserEdits = existing.some(row => row?.["isAutoSeed"] !== true);
  if (hasUserEdits) return existing;

  const isPreUploadPlaceholder =
    existing.length === 1 &&
    existing[0]?.["preset"] === "custom" &&
    existing[0]?.["isAutoSeed"] === true;
  if (isPreUploadPlaceholder && isNamedPlacementSeedEnabled(store)) {
    return seedFn();
  }

  return existing;
}
